import {
  getProperty,
  getPropertyAnytimeRate,
  getPropertyCalendarPrices,
  getPropertyRates,
} from 'api/accommodation'
import { MAX_ACCOMMODATION_CALENDAR_MONTHS } from 'constants/calendar'
import { beginningOfToday, diffInDays, formatIsoDate } from 'lib/datetime/dateUtils'
import { getChannelMarkupLeadPricingForDuration, getOfferChannelMarkupLead } from 'lib/channelMarkup/channelMarkupLeadPricing'
import type { ChannelMarkupLead, ChannelMarkupLeadOffer } from 'lib/channelMarkup/channelMarkupLeadPricing'
import { hasChannelMarkupV2PriceBand } from 'lib/channelMarkup/channelMarkupV2'
import type { ChannelMarkupSource } from 'lib/channelMarkup/channelMarkupV2'
import { getOffersById } from 'api/offer'
import { isBedbankOffer, isLEOffer } from 'lib/offer/offerTypes'
import { filterObject } from 'lib/object/objectUtils'
import buildPropertyCalendarPricesKey from 'lib/offer/buildPropertyCalendarPricesKey'
import buildPropertyRatesKey from 'lib/offer/buildPropertyRatesKey'
import getPropertyRatesReduxKeys from 'lib/offer/getPropertyRatesReduxKeys'
import rekeyPropertyRatesBySlot from 'lib/offer/rekeyPropertyRatesBySlot'
import { getChannelMarkupLeadOffer, getChannelMarkupSource } from 'selectors/channelMarkupSelector'
import { getSource, ghaPricedRequest, isPropertyCollectEnabled } from 'selectors/offerSelectors'
import { getGhaPriceMatchForProperty } from 'selectors/ghaPriceMatchSelectors'
import { selectRecommendedRateInheritance } from 'selectors/recommendedRateInheritanceSelectors'

import { API_CALL, PROPERTY_UPDATE_PRICING_DATA } from './actionConstants'
import {
  FETCH_PROPERTY,
  FETCH_PROPERTY_ANYTIME_RATE,
  FETCH_PROPERTY_CALENDAR_PRICES,
  FETCH_PROPERTY_RATES,
} from './apiActionConstants'
import type { AppAction } from './types/ActionTypes'
import type { ApiActionResult } from 'middlewares/apiMiddleware'

export function fetchProperty(propertyId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (!propertyId || state.offer.properties[propertyId]) return

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY,
      request: () => getProperty(propertyId, {
        regionCode: state.geo.currentRegionCode,
        currency: state.geo.currentCurrency,
        previewMode: state.system.previewMode,
      }),
      propertyId,
    })
  }
}

export function fetchPropertyAnytimeRate(propertyId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (!propertyId || state.offer.propertyAnytimeRates[propertyId]) return

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY_ANYTIME_RATE,
      request: () => getPropertyAnytimeRate(propertyId, {
        regionCode: state.geo.currentRegionCode,
        currency: state.geo.currentCurrency,
      }),
      propertyId,
    })
  }
}

interface PropertyCalendarPricesParams {
  /** Length of stay, in nights */
  duration: number
  /** Room occupancies */
  occupancies: Array<App.Occupants>
}

function isSingleNightForTwoAdults(duration: number, occupancies: Array<App.Occupants>): boolean {
  if (duration !== 1 || occupancies.length !== 1) return false
  const [{ adults, children, infants, teenagers }] = occupancies
  return adults === 2 && !children && !infants && !teenagers
}

export function fetchPropertyCalendarPrices(propertyId: string, {
  duration,
  occupancies,
}: PropertyCalendarPricesParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const key = buildPropertyCalendarPricesKey({
      propertyId,
      duration,
      occupancies,
      regionCode: state.geo.currentRegionCode,
      currency: state.geo.currentCurrency,
    })
    if (!key || state.offer.propertyCalendarPrices[key]) return
    const property = state.offer.properties[propertyId]?.property
    if (!property) return
    const promotion = property.promotion?.type === 'dynamicFlash' ? property.promotion : undefined
    if (promotion) {
      if (duration < promotion.minimumLos) return
    } else if (!isSingleNightForTwoAdults(duration, occupancies)) {
      return
    }
    const earliestIsoDate = formatIsoDate(beginningOfToday(property.location.timezone))
    // NB: When computing `latestIsoDate`, we're not using `addMonths`/`endOfMonth`/`formatIsoDate`
    // because they deal in the user's local time zone, which is impractical to test with our
    // current Jest setup (as we can't mock the system's time zone, which is fixed to UTC).
    //
    // Ideally, we'd be dealing with a naive (i.e. without time zone) date, but failing that, we're
    // sticking to UTC, which is free from DST-related quirks.
    //
    // First, we'll construct `latestDate` from `earliestIsoDate`. The time component is guaranteed
    // to be midnight UTC.
    const latestDate = new Date(earliestIsoDate)
    // Next, reset to the start of the month, so we don't potentially overshoot if `earliestIsoDate`
    // is near the end of the month.
    latestDate.setUTCDate(1)
    // Now add `MAX_ACCOMMODATION_CALENDAR_MONTHS` full months. Note that we've overshot by a month.
    latestDate.setUTCMonth(latestDate.getUTCMonth() + MAX_ACCOMMODATION_CALENDAR_MONTHS)
    // Now roll back to the end of the previous (i.e. correct) month.
    latestDate.setUTCMilliseconds(-1)
    const latestIsoDate = latestDate.toISOString().substring(0, 10)
    let startIsoDate = earliestIsoDate
    let endIsoDate = latestIsoDate
    if (promotion) {
      startIsoDate = promotion.travelFromIsoDate > earliestIsoDate ? promotion.travelFromIsoDate : earliestIsoDate
      // NB: `promotion.travelToIsoDate` is the latest that we can check out, not the latest that we
      // can check in.
      const latestTravelFromDate = new Date(promotion.travelToIsoDate)
      latestTravelFromDate.setUTCDate(latestTravelFromDate.getUTCDate() - duration)
      const latestTravelFromIsoDate = latestTravelFromDate.toISOString().substring(0, 10)
      endIsoDate = latestTravelFromIsoDate < latestIsoDate ? latestTravelFromIsoDate : latestIsoDate
    }
    if (startIsoDate > endIsoDate) return

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY_CALENDAR_PRICES,
      request: () => getPropertyCalendarPrices(propertyId, {
        startIsoYearMonth: startIsoDate.substring(0, 7),
        endIsoYearMonth: endIsoDate.substring(0, 7),
        duration,
        occupancies,
        currency: state.geo.currentCurrency,
        region: state.geo.currentRegionCode,
        isDynamicFlash: !!promotion,
      }).then(days => filterObject(days, (key) => startIsoDate <= key && key <= endIsoDate)),
      key,
    })
  }
}

interface PropertyRatesParams {
  checkIn: string
  checkOut: string
  occupancies: Array<App.Occupants>
  utmContent: string | undefined
  roomTypeId: string | undefined
  roomRateId: string | undefined
  roomRateGroupId?: string
  refresh?: boolean
  /** Called once the request settles; the only way to wait for the rates from a thunk. */
  onComplete?: (result: ApiActionResult<Record<string, { rooms?: Array<App.RawPropertyRoomRates> }>>) => void
}

/**
 * Leads of offers fetched only for their lead, by region and id, kept for the browser session so a
 * date or occupancy change does not fetch them again. Holds the fetch itself, so a fetch still in
 * flight is shared, and an offer public-offer did not return is kept as no lead rather than fetched
 * again. A failed fetch is dropped so the next change retries it. Only the lead is kept, not the
 * offer. The fetch is un-marked public-offer data, so it does not depend on the session's markup.
 * Never filled during SSR, where it would be shared by requests.
 */
const fetchedLeads = new Map<string, Promise<ChannelMarkupLead | undefined>>()

function getFetchedLeadKey(regionCode: string, offerId: string): string {
  return `${regionCode}:${offerId}`
}

export function resetFetchedChannelMarkupLeadsCache(): void {
  fetchedLeads.clear()
}

function fetchLeads(offerIds: Array<string>, regionCode: string): Map<string, Promise<ChannelMarkupLead | undefined>> {
  const leadsRequest = getOffersById(offerIds, regionCode).then(offers => new Map(offers
    .filter(offer => isLEOffer(offer) || isBedbankOffer(offer))
    .map(offer => [offer.id, getOfferChannelMarkupLead(offer)]),
  ))
  const isClient = typeof window !== 'undefined'
  return new Map(offerIds.map(offerId => {
    const key = getFetchedLeadKey(regionCode, offerId)
    const lead = leadsRequest.then(leads => leads.get(offerId))
    if (isClient) {
      fetchedLeads.set(key, lead)
      lead.catch(() => fetchedLeads.delete(key))
    }
    return [offerId, lead]
  }))
}

/**
 * Lead pricing for `nights` of the offers behind a property's rates — the LPC offers' lead package,
 * the bedbank offer's "from" sell — so they resolve a v2 price band on the same lead as the offer
 * page. A property page does not load those offers, so any that are not loaded are fetched — but
 * only when the session's markup has a price band, the one thing that needs them. An offer that
 * still has no lead fails the band closed.
 */
export async function resolvePropertyChannelMarkupLeadPricing(
  offerIds: Array<string>,
  {
    getLoadedOffer,
    nights,
    regionCode,
    markupSource,
  }: {
    /** Read when the rates arrive, so an offer that loaded while they were in flight is not fetched. */
    getLoadedOffer: (offerId: string) => ChannelMarkupLeadOffer | undefined
    nights: number
    regionCode: string
    markupSource: ChannelMarkupSource
  },
): Promise<Record<string, App.ChannelMarkupLeadPricing>> {
  const leadsByOfferId = new Map<string, ChannelMarkupLead | Promise<ChannelMarkupLead | undefined> | undefined>()
  const missingOfferIds: Array<string> = []
  for (const offerId of offerIds) {
    const loadedOffer = getLoadedOffer(offerId)
    const fetchedLead = fetchedLeads.get(getFetchedLeadKey(regionCode, offerId))
    if (loadedOffer) {
      leadsByOfferId.set(offerId, getOfferChannelMarkupLead(loadedOffer))
    } else if (fetchedLead) {
      leadsByOfferId.set(offerId, fetchedLead)
    } else {
      missingOfferIds.push(offerId)
    }
  }
  if (missingOfferIds.length && hasChannelMarkupV2PriceBand(markupSource)) {
    fetchLeads(missingOfferIds, regionCode).forEach((lead, offerId) => leadsByOfferId.set(offerId, lead))
  }
  const leadPricingByOfferId: Record<string, App.ChannelMarkupLeadPricing> = {}
  await Promise.all(Array.from(leadsByOfferId, async([offerId, lead]) => {
    // Without the offer the band fails closed, as for any unknown price.
    const leadPricing = getChannelMarkupLeadPricingForDuration(await Promise.resolve(lead).catch(() => undefined), nights)
    if (leadPricing) {
      leadPricingByOfferId[offerId] = leadPricing
    }
  }))
  return leadPricingByOfferId
}

export function fetchPropertyRates(propertyId: string, {
  checkIn,
  checkOut,
  occupancies,
  utmContent,
  roomTypeId,
  roomRateId,
  roomRateGroupId,
  refresh = false,
  onComplete,
}: PropertyRatesParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { source, gha } = ghaPricedRequest(getSource(state), getGhaPriceMatchForProperty(state, propertyId))

    const reduxKeys = getPropertyRatesReduxKeys({
      propertyId,
      checkIn,
      checkOut,
      occupancies,
      utmContent,
      roomTypeId,
      roomRateId,
    })
    if (!reduxKeys) return
    // NB: For better or worse, we have no TTL on cached responses, so rates _will_ become stale and
    // errors _won't_ clear until a hard refresh.
    const expectedKeys = [reduxKeys.combined, ...Object.values(reduxKeys.byOccupancy)]
    const allCached = !refresh && expectedKeys.every(key => !!state.offer.propertyRates[key]?.rooms)
    if (allCached) {
      onComplete?.({
        status: 'success',
        data: Object.fromEntries(expectedKeys.map(key => [key, { rooms: state.offer.propertyRates[key]?.rooms }])),
      })
      return
    }

    const reduxKeyBySlot: Record<string, string> = { combined: reduxKeys.combined, ...reduxKeys.byOccupancy }
    const markupSource = getChannelMarkupSource(state)

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY_RATES,
      request: () => getPropertyRates(propertyId, {
        checkIn,
        checkOut,
        occupancies,
        regionCode: state.geo.currentRegionCode,
        currency: state.geo.currentCurrency,
        previewMode: state.system.previewMode,
        source,
        gha,
        dynamicAvailability: true,
        enablePropertyCollect: isPropertyCollectEnabled(state),
        includeBundles: true,
        recommendedRateInheritance: occupancies.length === 1 ? selectRecommendedRateInheritance(state) : undefined,
        utmContent,
        roomTypeId,
        roomRateId,
        roomRateGroupId,
        areFlightsEnabled: state.offer.properties[propertyId]?.property?.areFlightsEnabled ?? false,
        markupSource,
        resolveChannelMarkupLeadPricing: offerIds => resolvePropertyChannelMarkupLeadPricing(offerIds, {
          getLoadedOffer: offerId => getChannelMarkupLeadOffer(getState(), offerId),
          nights: diffInDays(new Date(checkIn), new Date(checkOut)),
          regionCode: state.geo.currentRegionCode,
          markupSource,
        }),
      }).then(slotMap => rekeyPropertyRatesBySlot(slotMap, reduxKeyBySlot)),
      keys: reduxKeyBySlot,
      // The request resolves to bare room lists; hand them over in the same shape as the cached path.
      onComplete: onComplete && ((result: ApiActionResult<Record<string, Array<App.RawPropertyRoomRates>>>) => {
        onComplete(result.status === 'success' ?
            { status: 'success', data: Object.fromEntries(Object.entries(result.data).map(([key, rooms]) => [key, { rooms }])) } :
          result)
      }),
    })
  }
}

interface PropertyPricingDataParams {
  checkIn: string
  checkOut: string
  occupancies: Array<App.Occupants>
  utmContent: string | undefined
  roomTypeId: string | undefined
  roomRateId: string | undefined
  roomId: string
  rateId: string
  /** The new un-marked hotel price; see `PROPERTY_UPDATE_PRICING_DATA`. */
  newBasePrice: number
  newLuxPlusBasePrice: number | undefined
  newPropertyFees: number
}

export function updatePropertyPricingData(propertyId: string, {
  checkIn,
  checkOut,
  occupancies,
  utmContent,
  roomTypeId,
  roomRateId,
  roomId,
  rateId,
  newBasePrice,
  newLuxPlusBasePrice,
  newPropertyFees,
}: PropertyPricingDataParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const key = buildPropertyRatesKey({
      propertyId,
      checkIn,
      checkOut,
      occupancies,
      utmContent,
      roomTypeId,
      roomRateId,
    })
    if (!key || !state.offer.propertyRates[key]) return

    dispatch({
      type: PROPERTY_UPDATE_PRICING_DATA,
      key,
      roomId,
      rateId,
      newBasePrice,
      newLuxPlusBasePrice,
      newPropertyFees,
    })
  }
}
